import { ApiError } from "@bafe/api-client";
import type { ErrorCode } from "@bafe/types";

export interface PresentedError {
	/** Plain language, shown prominently. Never a raw code. */
	title: string;
	/** What to do next, when the code implies a specific next step. */
	description: string | null;
	/** Hidden behind "Technical details". */
	detail: string | null;
	requestId: string | null;
	retryable: boolean;
	status: number | null;
	fields: Record<string, string[]> | undefined;
}

/** Next steps that the error message itself cannot carry. */
const NEXT_STEP: Partial<Record<ErrorCode, string>> = {
	forbidden: "Your role does not include this action. An organisation owner or administrator can do it for you.",
	not_found: "It may have been removed, or the link may be out of date.",
	rate_limited: "Wait a moment, then try again.",
	provider_unavailable: "This is on our side. Try again shortly.",
	provisioning_failed: "We will keep retrying in the background. Contact support if it stays like this.",
	reauthentication_required: "Confirm your password to continue.",
	internal: "This is on our side. If it happens again, contact support with the reference below.",
};

function asApiError(error: unknown): ApiError | null {
	if (error instanceof ApiError) return error;
	if (error instanceof Error && error.name === "ApiError" && "code" in error && "status" in error) {
		return error as ApiError;
	}
	return null;
}

export function presentError(error: unknown): PresentedError {
	const apiError = asApiError(error);
	if (apiError) {
		const genericForbidden =
			apiError.code === "forbidden" &&
			/invitation was sent to|sign out|sign in as/i.test(apiError.message);
		return {
			title: apiError.message,
			description: genericForbidden ? null : (NEXT_STEP[apiError.code] ?? null),
			detail: apiError.detail ?? null,
			requestId: apiError.requestId && apiError.requestId.length > 0 ? apiError.requestId : null,
			retryable: apiError.retryable,
			status: apiError.status,
			fields: apiError.fields,
		};
	}

	if (error instanceof DOMException && error.name === "AbortError") {
		return {
			title: "That save was interrupted.",
			description: "Try again.",
			detail: error.message,
			requestId: null,
			retryable: true,
			status: null,
			fields: undefined,
		};
	}

	if (error instanceof Error) {
		return {
			title: error.message || "Something went wrong.",
			description: "Try again. If it keeps happening, contact support.",
			detail: error.message,
			requestId: null,
			retryable: true,
			status: null,
			fields: undefined,
		};
	}

	return {
		title: "Something went wrong.",
		description: "Try again. If it keeps happening, contact support.",
		detail: null,
		requestId: null,
		retryable: true,
		status: null,
		fields: undefined,
	};
}

/** First field-level message, for wiring a server rejection back to an input. */
export function fieldError(error: unknown, field: string): string | null {
	if (!(error instanceof ApiError)) return null;
	return error.fields?.[field]?.[0] ?? null;
}

export function isUnauthenticated(error: unknown): boolean {
	return error instanceof ApiError && error.code === "unauthenticated" && error.status === 401;
}

/**
 * The login endpoint answers with a field-level rejection when the account has
 * two-factor enabled and no code was supplied.
 */
export function requiresTwoFactor(error: unknown): boolean {
	if (!(error instanceof ApiError)) return false;
	if (error.code === "reauthentication_required") return true;
	return error.fields?.["totpCode"] !== undefined;
}
